/**
 * Portfolio Service
 * Handles all portfolio-related API calls to the auth-api backend.
 */

import { useAuthStore } from "../stores/authStore";
import { useSessionExpiredStore } from "../stores/sessionExpiredStore";

const API_BASE = import.meta.env.VITE_AUTH_API_URL || "http://localhost:8081";

// Types
export interface Portfolio {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  portfolio_type: "paper" | "live" | "simulated";
  cash_balance: number;
  initial_capital: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSummary extends Portfolio {
  total_value: number;
  invested_value: number;
  total_cost_basis: number;
  total_gain_loss: number;
  total_gain_loss_pct: number;
  today_change: number;
  today_change_pct: number;
  investment_count: number;
}

export interface Holding {
  id: string;
  portfolio_id: string;
  symbol: string;
  asset_type: string;
  quantity: number;
  average_cost: number;
  current_price: number;
  current_value: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  last_updated: string;
}

export interface StrategyInvestment {
  id: string;
  portfolio_id: string;
  strategy_id: string;
  allocation_percent: number;
  invested_amount: number;
  pending_delta: number; // Pending capital change: positive for deposits, negative for withdrawals
  current_value: number;
  is_active: boolean;
  started_at: string;
  ended_at?: string;
  total_return: number;
  total_return_pct: number;
}

export interface StrategyInvestmentWithDetails extends StrategyInvestment {
  strategy_name: string;
  strategy_slug: string;
  strategy_metrics?: {
    cagr?: number;
    sharpe?: number;
    max_drawdown?: number;
  };
  holdings_count: number;
  brokerage_account_id?: string;
  brokerage_account_name?: string;
  brokerage_account_type?: string;
  has_executed?: boolean; // True if investment has ever been executed (has real positions)
  first_executed_at?: string; // When investment was first executed
}

export interface Transaction {
  id: string;
  portfolio_id: string;
  strategy_id?: string;
  transaction_type:
    | "buy"
    | "sell"
    | "deposit"
    | "withdrawal"
    | "dividend"
    | "fee"
    | "split";
  symbol?: string;
  asset_type?: string;
  quantity?: number;
  price?: number;
  total_amount: number;
  commission: number;
  fees: number;
  notes?: string;
  executed_at: string;
  created_at: string;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
  limit: number;
  offset: number;
}

// Helper function for authenticated requests
async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  // Get token directly from Zustand store
  const token = useAuthStore.getState().accessToken;

  if (!token) {
    throw new Error("Not authenticated");
  }

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      console.warn("[portfolioService] 401 Unauthorized - triggering session expired modal");
      useSessionExpiredStore.getState().setExpired();
    }
    
    throw new Error(data.error || "Request failed");
  }

  return data as T;
}

// ============================================
// Portfolio
// ============================================

export async function getPortfolioSummary(): Promise<PortfolioSummary> {
  return authRequest<PortfolioSummary>("/api/v1/portfolio");
}

export async function getHoldings(): Promise<{
  holdings: Holding[];
  count: number;
}> {
  return authRequest<{ holdings: Holding[]; count: number }>(
    "/api/v1/portfolio/holdings",
  );
}

export async function getTransactions(
  limit = 20,
  offset = 0,
): Promise<TransactionsResponse> {
  return authRequest<TransactionsResponse>(
    `/api/v1/portfolio/transactions?limit=${limit}&offset=${offset}`,
  );
}

// ============================================
// Investments
// ============================================

export async function getInvestments(): Promise<{
  investments: StrategyInvestmentWithDetails[];
  count: number;
}> {
  return authRequest<{
    investments: StrategyInvestmentWithDetails[];
    count: number;
  }>("/api/v1/portfolio/investments");
}

export async function getInvestment(id: string): Promise<{
  investment: StrategyInvestment;
  strategy: import("./strategyService").Strategy;
}> {
  return authRequest<{
    investment: StrategyInvestment;
    strategy: import("./strategyService").Strategy;
  }>(`/api/v1/portfolio/investments/${id}`);
}

export async function closeInvestment(id: string): Promise<{
  message: string;
  cash_returned: number;
}> {
  return authRequest<{ message: string; cash_returned: number }>(
    `/api/v1/portfolio/investments/${id}`,
    {
      method: "DELETE",
    },
  );
}

export async function investInStrategy(
  strategyId: string,
  amount: number,
  brokerageAccountId: string,
): Promise<{
  investment: StrategyInvestment;
}> {
  if (!brokerageAccountId) {
    throw new Error("Brokerage account is required for investments");
  }
  return authRequest<{ investment: StrategyInvestment }>(
    `/api/v1/portfolio/investments`,
    {
      method: "POST",
      body: JSON.stringify({
        strategy_id: strategyId,
        amount: amount,
        brokerage_account_id: brokerageAccountId,
      }),
    },
  );
}

// ============================================
// Portfolio View (New Aggregated APIs)
// ============================================

// Account Summary for portfolio view - matches Go API response
export interface AccountSummary {
  id: string;
  account_name: string;
  account_type: "paper" | "live";
  portfolio_value: string;  // String from API, needs parsing
  cash?: string;  // Cash balance from Alpaca
  buying_power?: string;  // Buying power from Alpaca
  today_change: string;
  today_change_pct: string;
  is_default: boolean;
  investment_count: number;
  fractional_enabled: boolean;
}

// Strategy card data for portfolio view
// Note: API returns "id" for investment ID, but we alias as "investment_id" for clarity
export interface StrategyCard {
  id: string;  // This is the investment ID from the API
  strategy_id: string;
  strategy_name: string;
  brokerage_account_id: string;
  brokerage_account_name: string;
  brokerage_account_type: "paper" | "live";
  net_deposits: string;  // What user has contributed (deposits - withdrawals)
  cash_balance: string;  // Unallocated funds from non-fractional rounding
  current_value: string;
  today_change: string;
  today_change_pct: string;
  cumulative_return: string;
  cumulative_return_pct: string;
  threshold_pct: string;
  trading_setting: string;
  last_successful_trade?: string;
  holdings_count: number;
  is_active: boolean;
  has_executed: boolean;
  started_at: string;
  pending_delta: number;  // Pending capital change: positive for deposits, negative for withdrawals
}

// Holdings for a specific strategy
export interface StrategyHolding {
  symbol: string;
  name: string;
  quantity: number;
  avg_cost: number;
  current_price: number;
  current_value: number;
  unrealized_pnl: number;
  unrealized_pnl_pct: number;
  weight_pct: number; // Portfolio weight
  asset_type: string;
}

// Execution history entry
export interface ExecutionHistoryEntry {
  id: string;
  date: string;
  executed_at: string;
  execution_type: string;
  status: "success" | "partial" | "failed";
  orders_count: number;
  total_volume: string;
  portfolio_value_before: string;
  portfolio_value_after: string;
  orders: ExecutionOrder[];
  error_message?: string;

  // Shadow accounting audit fields
  shadow_positions_before?: ShadowPositionEntry[];
  alpaca_positions_before?: AlpacaPositionEntry[];
  intended_trades?: IntendedTradeEntry[];
  drift_detected?: DriftEntry[];
  scale_factor?: string;
  notes?: string;
}

// Shadow position snapshot entry
export interface ShadowPositionEntry {
  symbol: string;
  shares: string;
  cost_basis: string;
  investment_id?: string;
}

// Alpaca position snapshot entry
export interface AlpacaPositionEntry {
  symbol: string;
  qty: string;
  market_value: string;
  avg_entry_price: string;
  current_price: string;
  unrealized_pl: string;
}

// Intended trade entry from strategy allocation
export interface IntendedTradeEntry {
  symbol: string;
  action: string;
  shares: string;
  notional?: string;
  reason?: string;
}

// Drift detection entry
export interface DriftEntry {
  symbol: string;
  shadow_qty: string;
  alpaca_qty: string;
  drift: string;
  drift_pct?: string;
}

export interface ExecutionOrder {
  symbol: string;
  side: "buy" | "sell";
  quantity: number;
  filled_price: number;
  total_value: number;
  order_id: string;
  status: string;
  error_message?: string;
}

// Activity log entry types
export type ActivityEventType =
  | "investment_created"
  | "investment_cancelled"
  | "investment_liquidated"
  | "investment_closed"
  | "liquidation_started"
  | "liquidation_completed"
  | "liquidation_failed"
  | "deposit_queued"
  | "deposit_completed"
  | "withdrawal_queued"
  | "withdrawal_completed"
  | "execution_started"
  | "execution_completed"
  | "execution_failed"
  | "execution_skipped"
  | "manual_run_started"
  | "manual_run_completed"
  | "manual_run_failed"
  | "scheduled_run_started"
  | "scheduled_run_completed"
  | "scheduled_run_failed"
  | "rebalance_executed"
  | "market_closed"
  | "drift_detected"
  | "reconciliation_complete"
  | "position_adjusted"
  | "internal_transfer"
  | "pending_order_filled"
  | "pending_order_cancelled"
  | "insufficient_funds"
  | "symbol_delisted"
  | "insufficient_data"
  | "order_rejected";

export type ActivityCategory =
  | "lifecycle"
  | "execution"
  | "cash_flow"
  | "reconciliation"
  | "error";

export interface ActivityLogEntry {
  id: string;
  investment_id: string;
  event_type: ActivityEventType;
  category: ActivityCategory;
  title: string;
  description: string;
  details?: Record<string, unknown>;
  execution_log_id?: string;
  success?: boolean;
  error_code?: string;
  error_message?: string;
  occurred_at: string;
}

// Aggregated holding across all strategies
export interface AggregatedHolding {
  symbol: string;
  quantity: string;
  side: string;
  current_price: string;
  current_value: string;
  avg_cost: string;
  cost_basis: string;
  unrealized_pnl: string;
  unrealized_pnl_pct: string;
  today_pnl: string;
  today_pnl_pct: string;
  weight_pct: string;
}

// Portfolio View API Functions

export async function getPortfolioAccounts(accountId?: string): Promise<{
  accounts: AccountSummary[];
  count: number;
  total_equity: string;
  today_change: string;
  today_change_pct: string;
  total_cash?: string;
  total_buying_power?: string;
}> {
  const params = accountId ? `?account_id=${accountId}` : "";
  return authRequest<{
    accounts: AccountSummary[];
    count: number;
    total_equity: string;
    today_change: string;
    today_change_pct: string;
    total_cash?: string;
    total_buying_power?: string;
  }>(`/api/v1/portfolio-view/accounts${params}`);
}

export async function getPortfolioStrategies(accountId?: string): Promise<{
  strategies: StrategyCard[];
  totals: {
    total_value: string;
    today_change: string;
    today_change_pct: string;
    total_return: string;
    total_return_pct: string;
    cash_balance: string;
    investment_count: number;
  };
}> {
  const params = accountId ? `?account_id=${accountId}` : "";
  return authRequest<{
    strategies: StrategyCard[];
    totals: {
      total_value: string;
      today_change: string;
      today_change_pct: string;
      total_return: string;
      total_return_pct: string;
      cash_balance: string;
      investment_count: number;
    };
  }>(`/api/v1/portfolio-view/strategies${params}`);
}

export async function getStrategyHoldings(investmentId: string): Promise<{
  holdings: StrategyHolding[];
  strategy_name: string;
  total_value: number;
}> {
  return authRequest<{
    holdings: StrategyHolding[];
    strategy_name: string;
    total_value: number;
  }>(`/api/v1/portfolio-view/strategies/${investmentId}/holdings`);
}

export async function getStrategyHistory(
  investmentId: string,
  days = 30,
): Promise<{
  executions: ExecutionHistoryEntry[];
  strategy_name: string;
  total_count: number;
}> {
  return authRequest<{
    executions: ExecutionHistoryEntry[];
    strategy_name: string;
    total_count: number;
  }>(`/api/v1/portfolio-view/strategies/${investmentId}/history?days=${days}`);
}

export async function getStrategyActivity(
  investmentId: string,
  days = 60,
  category?: ActivityCategory,
): Promise<{
  investment_id: string;
  activities: ActivityLogEntry[];
  total: number;
}> {
  let url = `/api/v1/execution/investments/${investmentId}/activity?days=${days}`;
  if (category) {
    url += `&category=${category}`;
  }
  return authRequest<{
    investment_id: string;
    activities: ActivityLogEntry[];
    total: number;
  }>(url);
}

// Position snapshot types
export interface PositionSnapshotSummary {
  id: string;
  brokerage_account_id: string;
  strategy_investment_id?: string;
  reason: string;
  reason_detail?: string;
  actor_type: string;
  actor_id?: string;
  execution_log_id?: string;
  snapshot_at: string;
  created_at: string;
  item_count: number;
}

export interface PositionSnapshotItem {
  id: string;
  snapshot_id: string;
  strategy_investment_id: string;
  symbol: string;
  shares: string;
  cost_basis: string;
  market_price?: string;
  market_value?: string;
  fill_status?: string;
  pending_order_id?: string;
  investment_cash_balance?: string;
  investment_net_deposits?: string;
  investment_pending_delta?: string;
}

export interface PositionSnapshotDetail extends PositionSnapshotSummary {
  items: PositionSnapshotItem[];
}

export async function getPositionSnapshots(
  investmentId: string,
  limit = 50,
): Promise<PositionSnapshotSummary[]> {
  return authRequest<PositionSnapshotSummary[]>(
    `/api/v1/execution/investments/${investmentId}/position-snapshots?limit=${limit}`,
  );
}

export async function getPositionSnapshotDetail(
  snapshotId: string,
): Promise<PositionSnapshotDetail> {
  return authRequest<PositionSnapshotDetail>(
    `/api/v1/execution/position-snapshots/${snapshotId}`,
  );
}

export async function getAggregatedHoldings(): Promise<{
  holdings: AggregatedHolding[];
  total_value: number;
  unique_symbols: number;
}> {
  return authRequest<{
    holdings: AggregatedHolding[];
    total_value: number;
    unique_symbols: number;
  }>("/api/v1/portfolio-view/holdings");
}

export async function getPortfolioHoldings(accountId?: string): Promise<{
  holdings: AggregatedHolding[];
  total_value: string;
  total_cost_basis: string;
  total_unrealized: string;
  total_today_pnl: string;
  count: number;
  account_id?: string;
  account_name: string;
  data_as_of: string;
}> {
  const params = accountId ? `?account_id=${accountId}` : "";
  return authRequest<{
    holdings: AggregatedHolding[];
    total_value: string;
    total_cost_basis: string;
    total_unrealized: string;
    total_today_pnl: string;
    count: number;
    account_id?: string;
    account_name: string;
    data_as_of: string;
  }>(`/api/v1/portfolio-view/holdings${params}`);
}

// ============================================
// Portfolio Stats Helpers
// ============================================

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

export function getPnLColor(value: number): string {
  if (value > 0) return "text-green-600";
  if (value < 0) return "text-red-600";
  return "text-gray-600";
}

// ============================================
// Performance Chart
// ============================================

export interface PerformancePoint {
  date: string;
  portfolio_value: string;
  total_return_pct: string;
  day_return_pct: string;
  positions_count: number;
  is_live?: boolean; // True for intraday live data point
}

export interface StrategySeries {
  investment_id: string;
  strategy_name: string;
  color: string;
  start_date: string;
  data_points: PerformancePoint[];
}

export interface CashFlowMarker {
  date: string;
  type: "deposit" | "withdrawal";
  amount: string;
  investment_id: string;
}

export interface BenchmarkPoint {
  date: string;
  value: string;
  total_return_pct: string;
  is_live?: boolean; // True for intraday live data point
}

export interface PerformanceDataResponse {
  strategies: StrategySeries[];
  cash_flows: CashFlowMarker[];
  spy_benchmark: BenchmarkPoint[];
  from_date: string;
  to_date: string;
}

export interface UISettingsResponse {
  refresh_interval_seconds: number;
}

export type PerformanceTimeRange = "1M" | "3M" | "6M" | "1Y" | "YTD" | "ALL" | "CUSTOM";

// Get UI settings (refresh interval, etc.)
export async function getUISettings(): Promise<UISettingsResponse> {
  return authRequest<UISettingsResponse>("/api/v1/portfolio-view/settings");
}

// Get performance chart data for multiple strategies
export async function getPerformanceChartData(
  investmentIds: string[],
  range: PerformanceTimeRange = "ALL",
  customFrom?: string,
  customTo?: string,
): Promise<PerformanceDataResponse> {
  const params = new URLSearchParams();
  params.set("investment_ids", investmentIds.join(","));
  params.set("range", range);
  if (range === "CUSTOM" && customFrom && customTo) {
    params.set("from", customFrom);
    params.set("to", customTo);
  }
  return authRequest<PerformanceDataResponse>(
    `/api/v1/portfolio-view/performance-chart?${params.toString()}`
  );
}

// Update UI settings (Admin)
export async function updateUISettings(
  refreshIntervalSeconds: number
): Promise<{ refresh_interval_seconds: number; message: string }> {
  return authRequest<{ refresh_interval_seconds: number; message: string }>(
    "/api/v1/portfolio-view/settings",
    {
      method: "PUT",
      body: JSON.stringify({ refresh_interval_seconds: refreshIntervalSeconds }),
    }
  );
}

// ============================================
// Performance Report
// ============================================

export interface MonthlyReturn {
  year: number;
  month: number;
  account_return_pct: string;
  spy_return_pct: string;
  alpha_pct: string;
  account_start_value: string;
  account_end_value: string;
}

export interface YearlyReturn {
  year: number;
  account_return_pct: string;
  spy_return_pct: string;
  alpha_pct: string;
}

export interface EquityCurvePoint {
  date: string;
  account_value: string;
  spy_normalized: string;
}

export interface PerformanceSummary {
  ytd_account_pct: string;
  ytd_spy_pct: string;
  ytd_alpha_pct: string;
  total_account_pct: string;
  total_spy_pct: string;
  total_alpha_pct: string;
  inception_date: string;
}

export type PerformanceReportRange =
  | "YTD"
  | "1M"
  | "2M"
  | "3M"
  | "6M"
  | "1Y"
  | "2Y"
  | "3Y"
  | "5Y"
  | "ALL";

export interface PerformanceReportResponse {
  monthly_returns: MonthlyReturn[];
  yearly_returns: YearlyReturn[];
  equity_curve: EquityCurvePoint[];
  summary: PerformanceSummary;
}

export async function getPerformanceReport(
  accountId?: string,
  range: PerformanceReportRange = "ALL",
): Promise<PerformanceReportResponse> {
  const params = new URLSearchParams();
  if (accountId) params.set("account_id", accountId);
  params.set("range", range);
  return authRequest<PerformanceReportResponse>(
    `/api/v1/portfolio-view/performance-report?${params.toString()}`,
  );
}
